/**
 * Poll recent Dialpad call `Communication`s between a given practitioner role
 * and a patient, for the "select the call you're documenting" picker (DD8).
 *
 * There is no UI↔call correlation id, so instead of silently inferring "the
 * most recent call", we surface the candidate calls — scoped to the current
 * clinician's role and the patient — and let the clinician pick the right one.
 * Exposes `refetch` so the picker can re-poll while a just-ended call is still
 * being ingested by the webhook.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { Communication } from "@medplum/fhirtypes";
import { IDENTIFIER_SYSTEM } from "@benchmark/core/constants";
import { trackAction, TelemetryActions } from "@/app/boot/telemetry";

/** Extension URL carrying the Dialpad call direction. @agentic connections */
const CALL_DIRECTION_EXTENSION_URL =
  "https://benchmark.health/fhir/StructureDefinition/call-direction";

/** A selectable recent call. @agentic connections */
export type RecentCall = {
  communicationId: string;
  /** Whole minutes (rounded up, min 1). */
  minutes: number;
  /** Call start (Communication.sent), ISO. */
  callTime: string;
  /** Call end (Communication.received), ISO. */
  callEndTime: string;
  /** Direction, when recorded. */
  direction?: "inbound" | "outbound";
};

/** Options for {@link useRecentPatientCalls}. @agentic connections */
export type UseRecentPatientCallsOptions = {
  /** How far back to look, in minutes. Defaults to 720 (12h). */
  withinMinutes?: number;
};

/** Result of {@link useRecentPatientCalls}. @agentic connections */
export type UseRecentPatientCallsResult = {
  calls: RecentCall[];
  loading: boolean;
  refetch: () => void;
};

const DEFAULT_WINDOW_MINUTES = 720;

const directionOf = (
  communication: Communication,
): "inbound" | "outbound" | undefined => {
  const value = communication.extension?.find(
    (ext) => ext.url === CALL_DIRECTION_EXTENSION_URL,
  )?.valueCode;
  return value === "inbound" || value === "outbound" ? value : undefined;
};

const involvesRole = (
  communication: Communication,
  practitionerRoleId: string,
): boolean => {
  const ref = `PractitionerRole/${practitionerRoleId}`;
  if (communication.sender?.reference === ref) return true;
  return (communication.recipient ?? []).some((r) => r.reference === ref);
};

const toRecentCall = (communication: Communication): RecentCall | undefined => {
  if (!communication.id || !communication.sent || !communication.received) {
    return undefined;
  }
  const isDialpadCall = (communication.identifier ?? []).some(
    (id) => id.system === IDENTIFIER_SYSTEM.DIALPAD_CALL_ID,
  );
  if (!isDialpadCall) return undefined;
  const startMs = Date.parse(communication.sent);
  const endMs = Date.parse(communication.received);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return undefined;
  const durationMs = endMs - startMs;
  if (durationMs <= 0) return undefined;
  return {
    communicationId: communication.id,
    minutes: Math.max(1, Math.ceil(durationMs / 60_000)),
    callTime: communication.sent,
    callEndTime: communication.received,
    direction: directionOf(communication),
  };
};

/**
 * Hook: recent Dialpad calls between the practitioner role and the patient,
 * newest first. Best-effort — resolves to an empty list on error / missing
 * inputs. Re-poll via `refetch`.
 * @agentic connections
 */
export const useRecentPatientCalls = (
  patientId: string | undefined,
  practitionerRoleId: string | undefined,
  options: UseRecentPatientCallsOptions = {},
): UseRecentPatientCallsResult => {
  const medplum = useMedplum();
  const [calls, setCalls] = useState<RecentCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);
  const windowMinutes = options.withinMinutes ?? DEFAULT_WINDOW_MINUTES;
  const refetchCountRef = useRef(0);

  const refetch = useCallback(() => {
    refetchCountRef.current += 1;
    trackAction(TelemetryActions.CallsRefetch, {
      refetchCount: refetchCountRef.current,
    });
    setVersion((v) => v + 1);
  }, []);

  // Keep the latest loading flag setter usage out of the synchronous effect
  // body (react-hooks/set-state-in-effect): all setState happens in the async
  // callback below.
  const enabled = Boolean(patientId && practitionerRoleId);

  useEffect(() => {
    if (!patientId || !practitionerRoleId) {
      return;
    }
    let cancelled = false;
    const sinceIso = new Date(
      Date.now() - windowMinutes * 60_000,
    ).toISOString();

    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        const communications = await medplum.searchResources("Communication", {
          subject: `Patient/${patientId}`,
          sent: `ge${sinceIso}`,
          "status:not": "entered-in-error",
          _sort: "-sent",
          _count: "25",
        });
        if (cancelled) return;
        const recent = (communications ?? [])
          .filter((c) => involvesRole(c, practitionerRoleId))
          .map(toRecentCall)
          .filter((c): c is RecentCall => c !== undefined);
        setCalls(recent);
      } catch {
        if (!cancelled) setCalls([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, practitionerRoleId, windowMinutes, version]);

  return {
    calls: enabled ? calls : [],
    loading: enabled ? loading : false,
    refetch,
  };
};
